import { AuthType, FetchState, UserFeedbackEventAction } from "@/shared/types";
import { BiometricAuthCredsStoredKey } from "@/shared/localstoragekeys";
import {
    isMobileApp,
    NativePlatform, getAuthType
} from "@/shared/mobileHelper";
import { defineStore } from "pinia";
import _ from "lodash";
import { useUserFeedbackStore } from "@/employee/store/userFeedback";
import * as datadog from "@/shared/datadog";

export const ERROR_MESSAGE_BIOMETRIC_LOGIN_FAILED =
  getAuthType() === AuthType.Okta
  ? "Your biometric login has been disabled. Please check your device settings then re-enable biometric login within your account."
  : "Your biometric login has been disabled. Please check your device settings then select an option below to re-enable biometric login.";

export const ERROR_MESSAGE_BIOMETRIC_SETUP_FAILED = "Unable to complete your request at this moment. Please review the biometric settings on your device and try again.";

function formattedBiometricType (value: string): any {
    const biometryTypeModel = { type: value };
    switch (value) {
        case BiometryType.TouchId:
        case BiometryType.Biometric:
        case BiometryType.Finger:
            if (NativePlatform() === "ios") {
                return { ...biometryTypeModel, displayValue: "Touch ID" };
            }
            return { ...biometryTypeModel, displayValue: "Fingerprint" };
        case BiometryType.FaceId:
            return { ...biometryTypeModel, displayValue: "Face ID" };
        default:
            return { ...biometryTypeModel, displayValue: "none" };
    }
}

function ThrowErrorToUser (error: any): boolean {
    return error && error.code
        && error.code !== PluginError.BIOMETRIC_DISMISSED
        && error.code !== PluginError.BIOMETRIC_PIN_OR_PATTERN_DISMISSED;
}

export enum BiometryType {
    None = "none",
    TouchId = "touch",
    FaceId = "face",
    Finger = "finger",
    Biometric = "biometric"
}

export enum PluginError {
    BIOMETRIC_UNKNOWN_ERROR = -100,
    BIOMETRIC_UNAVAILABLE = -101,
    BIOMETRIC_AUTHENTICATION_FAILED = -102,
    BIOMETRIC_SDK_NOT_SUPPORTED = -103,
    BIOMETRIC_HARDWARE_NOT_SUPPORTED = -104,
    BIOMETRIC_PERMISSION_NOT_GRANTED = -105,
    BIOMETRIC_NOT_ENROLLED = -106,
    // When fingerprints are not enrolled, Samsung Galaxy S series returned an error code 11.
    BIOMETRIC_NOT_ENROLLED_II = 11,
    BIOMETRIC_INTERNAL_PLUGIN_ERROR = -107,
    BIOMETRIC_DISMISSED = -108,
    BIOMETRIC_PIN_OR_PATTERN_DISMISSED = -109,
    BIOMETRIC_SCREEN_GUARD_UNSECURED = -110,
    BIOMETRIC_LOCKED_OUT = -111,
    BIOMETRIC_LOCKED_OUT_PERMANENT = -112,
    BIOMETRIC_NO_SECRET_FOUND = -113,
}

export enum BiometricStep {
    SETUP = 0,
    LOGIN = 1
}

export interface BiometricAuthState {
    biometryType: BiometryType;
    biometricAuthEnabled: boolean;
    fetchState: FetchState;
    redirectToSetup: boolean;
}

export const initialState: BiometricAuthState = {
    biometryType: BiometryType.None,
    biometricAuthEnabled: null,
    fetchState: FetchState.Uninitialized,
    redirectToSetup: false
};

export const useBiometricAuthStore = defineStore("biometricAuth", {
    state: () => (_.cloneDeep(initialState)),
    getters: {
        getBiometryType: state => formattedBiometricType(state.biometryType),
        showBiometricSetup: state => state.biometryType !== BiometryType.None && !state.biometricAuthEnabled,
        isBiometricAuthAvailable: state => state.biometryType !== BiometryType.None,
        isBiometricAuthSettingsLoaded: state => state.fetchState === FetchState.Loaded,
    },
    actions: {
        setBiometryType (biometryType: BiometryType) {
            this.biometryType = biometryType;
        },
        setRedirectToSetup (value: boolean) {
            this.redirectToSetup = value;
        },
        setBiometricAuthEnabled (value: boolean) {
            this.biometricAuthEnabled = value;
        },
        setFetchState (fetchState: FetchState) {
            this.fetchState = fetchState;
        },
        loadBiometryType (): Promise<BiometryType> {
            return new Promise((resolve, reject) => {
                if (this.biometryType !== BiometryType.None && this.fetchState === FetchState.Loaded) {
                    return resolve(this.biometryType);
                }

                (window as any).Fingerprint
                    .isAvailable(
                        biometryType => {
                            resolve(biometryType);
                        },
                        error => {
                            datadog.log("Unable to load biometrics type.", error, "info");
                            reject(BiometryType.None);
                        }
                    );
            });
        },
        getLoginCreds (): Promise<any> {
            return new Promise((resolve, reject) => {
                (window as any).Fingerprint
                    .loadBiometricSecret({
                            title: "Biometrics Sign On",
                            description: "Log in using your biometrics credential.",
                            disableBackup: true, // always disabled on Android
                        },
                        result => {
                            if (result) {
                                resolve(JSON.parse(result));
                            }
                        },
                        error => {
                            if (ThrowErrorToUser(error)) {
                                datadog.log("Unable to load biometrics secret.", error, "info");
                                reject(ERROR_MESSAGE_BIOMETRIC_LOGIN_FAILED);
                            }
                        }
                    );
            });
        },
        loadBiometricSettings (): Promise<void> {
            if (!isMobileApp()) {
                return;
            }

            if (this.fetchState !== FetchState.Uninitialized) {
                return;
            }
            return new Promise((resolve, reject) => {
                this.setFetchState(FetchState.Loading);
                return this.loadBiometryType()
                    .then(biometryType => {
                        this.setBiometryType(biometryType);
                        const biometricsCredsKey = window.localStorage.getItem(BiometricAuthCredsStoredKey);
                        const isBiometricsAuthEnabled = biometricsCredsKey !== null;
                        this.setBiometricAuthEnabled(isBiometricsAuthEnabled);
                        this.setFetchState(FetchState.Loaded);
                        resolve();
                    })
                    .catch(error => {
                        datadog.log("Unable to load biometrics settings.", error, "info");
                        this.setFetchState(FetchState.Loaded);
                        this.setBiometryType(BiometryType.None);
                        resolve();
                    });
            });
        },
        enableBiometricAuth (args: { username: string, password: string }): Promise<any> {
            return new Promise((resolve, reject) => {
                datadog.log(`Enable biometrics login for loginUsername: ${args.username}.`, {}, "info");
                (window as any).Fingerprint
                    .registerBiometricSecret({
                            title: "Biometrics Setup",
                            subtitle: "",
                            description: "Use biometrics to complete the setup.",
                            fallbackButtonTitle: "Use pin to complete the setup.",
                            cancelButtonTitle: "Cancel",
                            confirmationRequired: false,
                            secret: JSON.stringify(args),
                            disableBackup: false,
                            invalidateOnEnrollment: true
                        },
                        success => {
                            window.localStorage.removeItem(BiometricAuthCredsStoredKey);
                            window.localStorage.setItem(BiometricAuthCredsStoredKey, "true");
                            this.setFetchState(FetchState.Uninitialized);
                            const userFeedbackStore = useUserFeedbackStore();
                            userFeedbackStore.setUserFeedbackAction(UserFeedbackEventAction.BiometricSettings);
                            resolve(true);
                        },
                        error => {
                            this.setFetchState(FetchState.Uninitialized);
                            if (ThrowErrorToUser(error)) {
                                datadog.log(`Unable to setup biometrics login for loginUsername: ${args.username}.`, error, "info");
                                reject(ERROR_MESSAGE_BIOMETRIC_SETUP_FAILED);
                            }
                        });
            });
        },
        disableBiometricAuth (): Promise<void> {
            return new Promise(resolve => {
                window.localStorage.removeItem(BiometricAuthCredsStoredKey);
                this.setBiometricAuthEnabled(false);
                this.setFetchState(FetchState.Uninitialized);
                resolve();
            });
        }
    }
});